import React from "react";
import { useNavigate } from "react-router-dom";
import { HiChevronRight } from "react-icons/hi2";
import { useAthletePrograms } from "../../hooks/useAthletePrograms";
import {
  formatAssigner,
  pickActiveProgram,
  programDetailPath,
} from "../../services/programService";
import { ProgramProgressBar } from "../programs/ProgramProgress";
import { formatProgramDateRange } from "../programs/programFormat";

interface ProgramProgressCardProps {
  userId: string;
  className?: string;
}

/**
 * Active-program card (plan 3g) shown at the top of the Schedule while a
 * program's window contains today: name, assigner, current week and
 * completed/total. Tapping opens the program detail. Renders nothing while
 * loading or without an active program so the layout does not jump.
 */
const ProgramProgressCard: React.FC<ProgramProgressCardProps> = ({
  userId,
  className = "",
}) => {
  const navigate = useNavigate();
  const { programs, loading } = useAthletePrograms(userId, "aroundToday");
  const program = loading ? null : pickActiveProgram(programs);
  if (!program) return null;

  const { progress } = program;
  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => navigate(programDetailPath(program))}
        data-testid="active-program-card"
        className="w-full text-left rounded-xl border border-base-300 bg-base-200 p-4 hover:bg-base-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-base-content/50 font-grotesk">
              Program ·{" "}
              <span className="font-geist tabular-nums">
                Week {progress.currentWeek} of {program.durationWeeks}
              </span>
            </div>
            <div className="font-semibold text-base-content font-heading truncate mt-0.5">
              {program.name}
            </div>
            <div className="text-xs text-base-content/60 font-grotesk mt-0.5 truncate">
              Assigned by {formatAssigner(program.assigner)}
              <span className="text-base-content/30"> · </span>
              <span className="font-geist tabular-nums">
                {formatProgramDateRange(progress.startDate, progress.endDate)}
              </span>
            </div>
          </div>
          <HiChevronRight className="text-xl text-base-content/40 flex-shrink-0 mt-0.5" />
        </div>
        <ProgramProgressBar progress={progress} className="mt-3" />
      </button>
    </div>
  );
};

export default ProgramProgressCard;
